import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { EnvelopeIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/AuthContext';
import { useEmailVerificationPoll } from '../../hooks/useEmailVerificationPoll';
import { resendRegistrationVerificationEmail } from '../../utils/resendVerificationEmail';
import {
  EMAIL_VERIFIED_EVENT,
  hasEmailVerifiedSession,
  isClientEmailVerified,
  mustRequireEmailVerification,
  readPendingRegistration,
  syncEmailVerifiedFromAuth,
} from '../../utils/registrationFlow';

function dismissKey(uid) {
  return uid ? `emailVerifyBannerDismissed_${uid}` : 'emailVerifyBannerDismissed';
}

/**
 * Soft, dismissible clinic banner — does not block the product.
 * Hides as soon as Auth, Firestore, or a verify-success event says the email is confirmed.
 */
export default function EmailVerifyBanner() {
  const { user, userData, refreshUserData } = useContext(AuthContext);
  const [dismissed, setDismissed] = useState(true);
  const [verified, setVerified] = useState(() => {
    const u = getAuth().currentUser;
    return isClientEmailVerified(u, null) || hasEmailVerifiedSession(u?.uid);
  });
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  const email = user?.email || userData?.email || '';
  const uid = user?.uid || userData?.uid || '';

  const markVerified = useCallback(() => {
    setVerified(true);
  }, []);

  useEffect(() => {
    if (!uid) {
      setDismissed(true);
      return;
    }
    setDismissed(sessionStorage.getItem(dismissKey(uid)) === 'true');
    if (
      isClientEmailVerified(getAuth().currentUser, userData) ||
      hasEmailVerifiedSession(uid)
    ) {
      setVerified(true);
    }
  }, [uid, userData?.emailVerified]);

  // Hard sync on mount / uid change — catches verify completed in another tab or via email link
  useEffect(() => {
    if (!uid || verified) return undefined;
    let cancelled = false;
    (async () => {
      const ok = await syncEmailVerifiedFromAuth({
        userData: { emailVerified: userData?.emailVerified },
      });
      if (cancelled) return;
      if (ok) {
        setVerified(true);
        try {
          await refreshUserData?.();
        } catch (_) {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, verified, userData?.emailVerified, refreshUserData]);

  useEffect(() => {
    const onVerifiedEvent = () => setVerified(true);
    const onStorage = (e) => {
      if (!uid) return;
      if (e.key === `emailVerifiedSession_${uid}` && e.newValue === 'true') {
        setVerified(true);
      }
    };
    window.addEventListener(EMAIL_VERIFIED_EVENT, onVerifiedEvent);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EMAIL_VERIFIED_EVENT, onVerifiedEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [uid]);

  const bannerVisible =
    !!uid && !verified && !dismissed && !!email && mustRequireEmailVerification(userData);

  useEmailVerificationPoll({
    enabled: bannerVisible,
    onVerified: () => {
      markVerified();
      refreshUserData?.();
    },
  });

  // Soft banner only for new self-registrations that opted into the verify gate.
  if (!mustRequireEmailVerification(userData)) return null;
  if (!uid || verified || dismissed || !email) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey(uid), 'true');
    setDismissed(true);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setResent(false);
    try {
      // Re-check before resending — avoids asking for confirm when already verified
      const ok = await syncEmailVerifiedFromAuth({ userData });
      if (ok) {
        setVerified(true);
        await refreshUserData?.();
        return;
      }
      const pending = readPendingRegistration();
      await resendRegistrationVerificationEmail({
        email,
        practiceName:
          pending?.practiceName ||
          userData?.practiceName ||
          userData?.displayName ||
          user?.displayName ||
          '',
      });
      setResent(true);
    } catch (err) {
      setError(err.message || 'Could not resend email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="border-b border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2.5 sm:px-6">
      <div className="mx-auto flex max-w-5xl items-start gap-3 sm:items-center">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800 sm:mt-0">
          <EnvelopeIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-amber-950">
            <span className="font-semibold">Email not verified</span>
            {' — '}
            we&apos;ve sent a link to{' '}
            <span className="font-medium break-all">{email}</span>.
          </p>
          {(resent || error) && (
            <p className={`mt-1 text-xs ${error ? 'text-red-600' : 'text-emerald-700'}`}>
              {error || 'Email sent again — check inbox and spam.'}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm ring-1 ring-inset ring-amber-200 hover:bg-amber-50 disabled:opacity-60"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Sending…' : 'Resend'}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="rounded-lg p-1.5 text-amber-800/70 hover:bg-white/80 hover:text-amber-950"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
